import { tenantHeaders } from './apiClient';
import { apiRequest } from './httpRequest';
import type { ReactionDrawingDocument } from '@peruselab/structure-editor/chemistry/reactionParticipant';
import type { ChemMoleculeV1Document } from '@peruselab/structure-editor/domain/moleculeProjection';
import type { ChemIsomerV1Document } from '@peruselab/structure-editor/domain/isomerDocument';
import type { DrawingVisibility } from './moleculeDrawings';

export interface ReactionResponse {
  readonly id: string;
  readonly documentRevision: number;
  readonly title: string | null;
  readonly schemaVersion: string;
  readonly document: ReactionDrawingDocument;
  readonly ownerType: string;
  /** Absent for signed-out readers of a public reaction (#1124). */
  readonly ownerId?: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** #718 -- set while the reaction is archived (out of the list and search). */
  readonly archivedAt?: string | null;
  /** #1124 -- public with its drawing; `owned` says whether the reader may change it. */
  readonly visibility?: DrawingVisibility;
  readonly owned?: boolean;
  /** #1124 -- who drew it; null when none has been recorded. */
  readonly authorName?: string | null;
}

/** The participant roles a reaction can hold (ADR 0008). */
export type ReactionRole = 'reactant' | 'product' | 'reagent' | 'catalyst' | 'solvent';

/** #720 -- one participant in full: its role, isomer and molecule. */
export interface ExpandedReactionParticipant {
  readonly role: ReactionRole;
  readonly isomer: {
    readonly id: string;
    readonly documentRevision: number;
    readonly moleculeId: string;
    readonly document: ChemIsomerV1Document;
  };
  readonly molecule: {
    readonly id: string;
    readonly documentRevision: number;
    readonly document: ChemMoleculeV1Document;
  };
}

export async function createReaction(document: ReactionDrawingDocument, title?: string): Promise<ReactionResponse> {
  const normalizedTitle = title?.trim();
  const query = normalizedTitle ? `?${new URLSearchParams({ title: normalizedTitle })}` : '';
  return apiRequest<ReactionResponse>(`/reactions${query}`, 'reaction', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(document)
  });
}

/** #718 -- replaces a saved reaction (revision-checked: a stale `documentRevision` is a 409). The
 *  title is left as it is unless given. */
export async function updateReaction(
  id: string,
  documentRevision: number,
  document: ReactionDrawingDocument,
  title?: string,
  tenant?: string | null
): Promise<ReactionResponse> {
  return apiRequest<ReactionResponse>(`/reactions/${encodeURIComponent(id)}`, 'reaction', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...tenantHeaders(tenant) },
    body: JSON.stringify({ documentRevision, document, ...(title !== undefined ? { title: title.trim() } : {}) })
  });
}

/** #718 -- archives a reaction (kept, but out of the list and search) or restores it. */
export async function setReactionArchived(
  id: string,
  documentRevision: number,
  archived: boolean,
  tenant?: string | null
): Promise<ReactionResponse> {
  return apiRequest<ReactionResponse>(
    `/reactions/${encodeURIComponent(id)}/${archived ? 'archive' : 'restore'}`,
    'reaction',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...tenantHeaders(tenant) },
      body: JSON.stringify({ documentRevision })
    }
  );
}

/** #1124 -- the owner makes a reaction public or private, as a drawing (#811). */
export async function setReactionVisibility(
  id: string,
  visibility: DrawingVisibility,
  tenant?: string | null
): Promise<ReactionResponse> {
  return apiRequest<ReactionResponse>(`/reactions/${encodeURIComponent(id)}/visibility`, 'reaction', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...tenantHeaders(tenant) },
    body: JSON.stringify({ visibility })
  });
}

/** #1124 (ADR 0019 D13) -- the reaction's molecules still private to its tenant; the publish prompt lists them. */
export async function listReactionPrivateMolecules(id: string, tenant?: string | null): Promise<readonly string[]> {
  return apiRequest<readonly string[]>(`/reactions/${encodeURIComponent(id)}/private-molecules`, 'reaction', {
    headers: { ...tenantHeaders(tenant) }
  });
}

// #139: fetching an already-saved Reaction, needed to render it as an Illustration element
// (ReactionThumbnail) -- GET /api/reactions/{id} has existed server-side since #138.
export async function getReaction(id: string): Promise<ReactionResponse> {
  return apiRequest<ReactionResponse>(`/reactions/${encodeURIComponent(id)}`, 'reaction');
}

/** #720 -- a reaction with every participant's isomer and molecule, in document order: all that
 *  opening it onto the paper needs, in one request. */
export async function getReactionWithParticipants(
  id: string
): Promise<ReactionResponse & { readonly participants: readonly ExpandedReactionParticipant[] }> {
  return apiRequest(`/reactions/${encodeURIComponent(id)}?expand=participants`, 'reaction');
}

export interface ReactionSummary {
  readonly id: string;
  readonly documentRevision: number;
  readonly title: string | null;
  readonly schemaVersion: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ReactionPage {
  readonly content: readonly ReactionSummary[];
  readonly page: number;
  readonly size: number;
  readonly totalElements: number;
  readonly totalPages: number;
}

export type ReactionSort = 'updatedAt' | 'createdAt' | 'title';
export type ReactionSortDirection = 'asc' | 'desc';

// #140: listing the user's own saved Reactions, needed by ReactionPicker -- GET /api/reactions
// has existed server-side since #138.
export async function listReactions(
  page = 0,
  size = 20,
  query?: string,
  sort: ReactionSort = 'updatedAt',
  direction: ReactionSortDirection = 'desc',
  // #720 -- only the reactions this molecule takes part in (in this role, when given).
  molecule?: { readonly moleculeId: string; readonly role?: ReactionRole }
): Promise<ReactionPage> {
  const params = new URLSearchParams({ page: String(page), size: String(size), sort, direction });
  if (query?.trim()) params.set('query', query.trim());
  if (molecule) {
    params.set('moleculeId', molecule.moleculeId);
    if (molecule.role) params.set('role', molecule.role);
  }
  return apiRequest<ReactionPage>(`/reactions?${params}`, 'reaction');
}

/** #1124 -- one participant as a public reaction's page shows it; `moleculeId` and `name` only when public. */
export interface PublicReactionParticipant {
  readonly role: ReactionRole;
  readonly coefficient: number | null;
  readonly moleculeId: string | null;
  readonly name: string | null;
  readonly molecularFormula: string | null;
  readonly smiles: string | null;
}

/** #1124 -- a public reaction's page, the same account the server writes into `/r/<id>`. */
export interface PublicReactionPage {
  readonly id: string;
  readonly title: string | null;
  /** Its title, or its scheme ("A + B → C") when it has none. */
  readonly label: string;
  readonly authorName: string | null;
  readonly updatedAt: string;
  readonly arrowKind: string;
  readonly overlay: string | null;
  readonly participants: readonly PublicReactionParticipant[];
  readonly conditions: {
    readonly temperatureC: number | null;
    readonly pressureAtm: number | null;
    readonly durationS: number | null;
    readonly yieldPercent: number | null;
    readonly notes: string | null;
  };
}

/** #1124 -- a public reaction's page; signed-out visitors may read it. */
export async function getPublicReactionPage(id: string): Promise<PublicReactionPage> {
  return apiRequest<PublicReactionPage>(`/reactions/${encodeURIComponent(id)}/page`, 'reaction');
}

/** #1136 -- one public reaction as the public home lists it. */
export interface PublicReactionSummary {
  readonly id: string;
  readonly title: string | null;
  /** Its title, or its scheme ("A + B → C") when it has none. */
  readonly label: string;
  readonly authorName: string | null;
  readonly updatedAt: string;
}

export interface PublicReactionList {
  readonly content: readonly PublicReactionSummary[];
  readonly page: number;
  readonly size: number;
  readonly totalElements: number;
  readonly totalPages: number;
}

/** #1136 -- anyone's public reactions, most recently updated first; signed-out visitors may read it. */
export async function listPublicReactions(page = 0, size = 24): Promise<PublicReactionList> {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  return apiRequest<PublicReactionList>(`/reactions/public?${params}`, 'reaction');
}
